import { Component } from '@theme/component';

/**
 * @typedef {Object} AerieCardSliderRefs
 * @property {HTMLElement} track
 * @property {HTMLButtonElement} [prev]
 * @property {HTMLButtonElement} [next]
 */

/**
 * Slider de tarjetas Aerie con reveal animado y flechas condicionales.
 * @extends {Component<AerieCardSliderRefs>}
 */
class AerieCardSlider extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.refs.track.addEventListener('scroll', () => this.#updateState(), { passive: true, signal });

    this.resizeObserver = new ResizeObserver(() => this.#updateState());
    this.resizeObserver.observe(this.refs.track);

    requestAnimationFrame(() => this.#updateState());

    this.#setupReveal();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
  }

  #updateState() {
    const track = this.refs.track;
    const overflows = track.scrollWidth > track.clientWidth + 1;
    const canScrollLeft = track.scrollLeft > 1;
    const canScrollRight = track.scrollLeft < track.scrollWidth - track.clientWidth - 1;

    this.toggleAttribute('overflows', overflows);
    this.toggleAttribute('can-scroll-left', canScrollLeft);
    this.toggleAttribute('can-scroll-right', canScrollRight || (overflows && !canScrollLeft));
  }

  #setupReveal() {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.setAttribute('revealed', '');
            this.intersectionObserver.disconnect();
          }
        }
      },
      { threshold: 0.1 }
    );
    this.intersectionObserver.observe(this);
  }

  scrollPrev() {
    this.#scrollBy(-1);
  }

  scrollNext() {
    this.#scrollBy(1);
  }

  #scrollBy(direction) {
    const track = this.refs.track;
    const card = track.querySelector('.aerie-card');
    const gap = parseInt(getComputedStyle(track).gap) || 0;
    const step = (card?.offsetWidth ?? track.clientWidth / 2) + gap;
    track.scrollBy({ left: direction * step, behavior: 'smooth' });
  }
}

customElements.define('aerie-card-slider', AerieCardSlider);
